import dayjs from 'dayjs';

import { clampedShipDay } from './laneForm';
import type {
  AirCargoRequest,
  LaneMode,
  LaneSearchRequest,
  OceanCargoRequest,
  RoadCargoRequest,
  SavedLaneQuote,
  SavedLaneSearch,
} from '../../../apis/lanePlanner';

const AIR_GOODS_LABELS: Record<string, string> = {
  pharmaceuticals: 'Pharma',
  perishables: 'Perishables',
  special_cargo: 'Special cargo',
  other: 'Other',
};

const oceanSummary = (ocean: OceanCargoRequest): string[] => {
  const parts: string[] = [ocean.shipment_type];
  const items = ocean.items ?? [];
  if (ocean.shipment_type === 'FCL') {
    const bySize = new Map<string, number>();
    for (const item of items) {
      const size = item.container_size_type || 'CTR';
      bySize.set(size, (bySize.get(size) || 0) + (item.quantity || 0));
    }
    const sizes = [...bySize.entries()]
      .filter(([, qty]) => qty > 0)
      .map(([size, qty]) => `${qty}×${size}`);
    if (sizes.length) parts.push(sizes.join(', '));
  } else {
    const byKind = new Map<string, number>();
    for (const item of items) {
      const kind = item.unit_type === 'BOX' ? 'box' : 'pallet';
      byKind.set(kind, (byKind.get(kind) || 0) + (item.quantity || 0));
    }
    const units = [...byKind.entries()]
      .filter(([, qty]) => qty > 0)
      .map(
        ([kind, qty]) =>
          `${qty} ${kind}${qty === 1 ? '' : kind === 'box' ? 'es' : 's'}`
      );
    if (units.length) parts.push(units.join(', '));
    const weightKg = items.reduce(
      (sum, item) => sum + (Number(item.weight_kg) || 0) * (item.quantity || 0),
      0
    );
    if (weightKg > 0) parts.push(`${Math.round(weightKg)} kg`);
    if (ocean.totals_only) {
      const volume = items.reduce(
        (sum, item) => sum + (Number(item.volume_m3) || 0),
        0
      );
      if (volume > 0) parts.push(`${volume.toFixed(2)} m³`);
    }
  }
  if (ocean.hazmat) parts.push('Hazmat');
  return parts;
};

const airSummary = (air: AirCargoRequest): string[] => {
  const parts: string[] = [];
  parts.push(
    air.service_level === 'passive_temp'
      ? 'Temp-controlled'
      : air.service_level === 'active_container'
        ? 'Active container'
        : (air.goods_category && AIR_GOODS_LABELS[air.goods_category]) ||
          'General'
  );
  const ulds = air.ulds ?? [];
  const pieces = air.pieces ?? [];
  if (ulds.length) {
    const units = ulds.reduce((sum, uld) => sum + (uld.units || 0), 0);
    const types = [...new Set(ulds.map((uld) => uld.uld_type).filter(Boolean))];
    parts.push(
      `${units} ULD${units === 1 ? '' : 's'}${types.length ? ` (${types.join(', ')})` : ''}`
    );
    const weight = ulds.reduce(
      (sum, uld) => sum + (Number(uld.gross_weight_kg) || 0),
      0
    );
    if (weight > 0) parts.push(`${Math.round(weight)} kg`);
  } else if (pieces.length) {
    const units = pieces.reduce((sum, piece) => sum + (piece.pieces || 0), 0);
    parts.push(`${units} piece${units === 1 ? '' : 's'}`);
    const weight = pieces.reduce(
      (sum, piece) =>
        sum +
        (Number(piece.gross_weight_kg) || 0) *
          (piece.weight_per_item ? piece.pieces || 0 : 1),
      0
    );
    if (weight > 0) parts.push(`${Math.round(weight)} kg`);
  }
  if (air.lithium_battery && air.lithium_battery !== 'NONE') {
    parts.push('Batteries');
  }
  return parts;
};

const roadSummary = (road: RoadCargoRequest): string[] => {
  if (road.type === 'ltl') return ['LTL', `${road.pallets} pallets`];
  if (road.type === 'parcel') return ['Parcel', `${road.packages} packages`];
  return ['FTL', road.equipment];
};

const SUMMARIES: Record<LaneMode, (r: LaneSearchRequest) => string[]> = {
  air: (r) => (r.air ? airSummary(r.air) : []),
  ocean: (r) => (r.ocean ? oceanSummary(r.ocean) : []),
  road: (r) => (r.road ? roadSummary(r.road) : []),
};

/** Compact cargo line for a saved search card, one group per searched mode. */
export const formatSearchCargo = (
  request: LaneSearchRequest
): string | null => {
  const groups = request.modes
    .map((mode) => SUMMARIES[mode](request).join(' · '))
    .filter(Boolean);
  return groups.length ? groups.join(' | ') : null;
};

/** Strictly before today: a rate valid to today is still usable. */
const isPastDay = (iso?: string | null): boolean =>
  !!iso && dayjs(iso).isBefore(dayjs(), 'day');

export const searchExpired = (saved: SavedLaneSearch): boolean =>
  isPastDay(saved.ship_date);

/** A quote is stale once its validity ends (timestamps at the instant) or its ship date has passed. */
export const quoteExpired = (saved: SavedLaneQuote): boolean => {
  const validUntil = saved.quote.valid_until;
  if (validUntil) {
    // A date-only value is valid through the end of that day.
    const end = /^\d{4}-\d{2}-\d{2}$/.test(validUntil.trim())
      ? dayjs(validUntil).endOf('day')
      : dayjs(validUntil);
    if (end.isBefore(dayjs())) return true;
  }
  return isPastDay(saved.ship_date);
};

const withoutTempRange = ({
  temp_range: _tempRange,
  ...air
}: NonNullable<LaneSearchRequest['air']>) => {
  void _tempRange;
  return air;
};

/**
 * A stored search ready to run again: the ship date moves to the earliest day
 * vendors accept (the day the form shows), and the storage-only
 * `schema_version` is dropped because the search API rejects unknown fields.
 * A stored air temperature range is dropped too: the form no longer asks one.
 */
export const rerunnableRequest = (
  request: LaneSearchRequest
): LaneSearchRequest => {
  const { schema_version: _schemaVersion, ...rest } =
    request as LaneSearchRequest & { schema_version?: number };
  void _schemaVersion;
  return {
    ...rest,
    ...(rest.air && { air: withoutTempRange(rest.air) }),
    ship_date: clampedShipDay(request.ship_date, request.modes).format(
      'YYYY-MM-DD'
    ),
  };
};
